import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Check, ChevronsUpDown } from 'lucide-react';

/**
 * Aranabilir ürün seçici.
 *
 * Uzun listelerde düz açılır liste kullanışsız: 1300 ürün arasında kaydırarak
 * aramak gerekiyordu. Bu bileşen ad ve koda göre arar.
 *
 * Türkçe karakter notu: "İ".toLowerCase() JavaScript'te i + birleşen nokta
 * (iki karakter) üretir, bu yüzden düz küçük harfe çevirme "İPLİK" ile
 * "iplik"i eşleştiremez. Arama için Türkçe harfler ASCII karşılığına
 * indirgenir — böylece "sekar" yazınca "Şeker" de bulunur.
 */
const trNorm = (s) => (s || '')
  .replace(/[İIı]/g, 'i').replace(/[çÇ]/g, 'c').replace(/[ğĞ]/g, 'g')
  .replace(/[öÖ]/g, 'o').replace(/[şŞ]/g, 's').replace(/[üÜ]/g, 'u')
  .toLowerCase();

const AZAMI_SONUC = 80;

export default function ProductCombobox({
  value,
  products = [],
  onChange,
  placeholder = 'Ürün ara...',
  searchPlaceholder = 'Ad veya kod ile ara...',
  emptyText = 'Ürün bulunamadı',
  disabled = false,
  className = '',
  testId,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = trNorm(query.trim());
    if (!q) return products.slice(0, AZAMI_SONUC);
    // Kelime kelime ara: "dana kiy" -> "Dana Kıyma" bulunur
    const parcalar = q.split(/\s+/);
    return products
      .filter((p) => {
        const hedef = `${trNorm(p.name)} ${trNorm(p.code)}`;
        return parcalar.every((parca) => hedef.includes(parca));
      })
      .slice(0, AZAMI_SONUC);
  }, [products, query]);

  const secili = products.find((p) => p.id === value);
  const gizlenen = query.trim()
    ? 0
    : Math.max(0, products.length - filtered.length);

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(''); }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId}
          className={`w-full justify-between border-[#d0c4b0] bg-white text-[#1a1916]
                      hover:bg-white hover:border-[#b0a090] font-normal ${className}`}
        >
          {secili ? (
            <span className="truncate text-left">
              {secili.name}
              <span className="text-[#8a7e70]"> ({secili.code})</span>
            </span>
          ) : (
            <span className="text-[#8a7e70] truncate">{placeholder}</span>
          )}
          <ChevronsUpDown size={13} className="ml-2 shrink-0 text-[#8a7e70]" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[300px] p-0" align="start">
        {/* shouldFilter=false: suzme yukarida, Turkce duyarli yapiliyor.
            onKeyDown: hicbir sey yazilmadan Enter'a basilinca listenin ILK
            ogesi seciliyordu — kullanici farkinda olmadan alakasiz bir urun
            secebiliyor. Arama yapilmadan Enter ile secime izin verme. */}
        <Command
          shouldFilter={false}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter' && !query.trim()) ev.preventDefault();
          }}
        >
          <CommandInput placeholder={searchPlaceholder} value={query} onValueChange={setQuery} />
          <CommandList className="max-h-64">
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {filtered.map((p) => (
                <CommandItem
                  key={p.id}
                  value={p.id}
                  onSelect={() => { onChange(p.id); setQuery(''); setOpen(false); }}
                >
                  <Check size={13} className={`mr-2 shrink-0 ${value === p.id ? 'opacity-100' : 'opacity-0'}`} />
                  <span className="truncate">{p.name}</span>
                  <span className="ml-1.5 text-xs text-[#8a7e70] shrink-0">
                    {p.code}{p.unit ? ` · ${p.unit}` : ''}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
            {gizlenen > 0 && (
              <div className="px-3 py-2 text-xs text-[#8a7e70] border-t border-[#e3dbcd]">
                {products.length} üründen ilk {filtered.length} tanesi gösteriliyor — aramak için yazın
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
